import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Gift, Check, Loader2, Sparkles, AlertCircle, ArrowRight, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export default function GiftRedeemPage() {
  const { giftCode } = useParams();
  const navigate = useNavigate();
  const { token, user, updateUser } = useAuth();
  const [giftInfo, setGiftInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [redeeming, setRedeeming] = useState(false);
  const [redeemed, setRedeemed] = useState(null);
  const [manualCode, setManualCode] = useState(giftCode || "");

  useEffect(() => {
    if (giftCode) fetchGiftInfo(giftCode);
    else setLoading(false);
  }, [giftCode]);

  const fetchGiftInfo = async (code) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/gift/verify/${code}`);
      setGiftInfo(data);
    } catch {
      setGiftInfo(null);
    } finally {
      setLoading(false);
    }
  };

  const handleRedeem = async () => {
    if (!token) {
      toast.error("Please log in to redeem your gift");
      navigate("/auth");
      return;
    }
    const code = giftCode || manualCode.trim();
    if (!code) return;
    setRedeeming(true);
    try {
      const { data } = await axios.post(`${API}/gift/redeem`, { gift_code: code }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRedeemed(data);
      if (data.tier) updateUser({ tier: data.tier });
      toast.success(`${data.tier_name} activated!`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to redeem gift");
    } finally {
      setRedeeming(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  if (redeemed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="w-full max-w-md text-center space-y-6 animate-in fade-in zoom-in-95">
          <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center">
            <Sparkles className="w-10 h-10 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-heading font-bold mb-2">You&apos;re all set!</h1>
            <p className="text-muted-foreground text-sm">
              {redeemed.sender_name} gifted you <strong>{redeemed.tier_name}</strong> for {redeemed.duration_label}
            </p>
          </div>
          {redeemed.note && (
            <div className="bg-muted/30 rounded-2xl p-4 text-sm italic text-muted-foreground">
              &ldquo;{redeemed.note}&rdquo;
              {!redeemed.anonymous && <p className="text-[11px] mt-2 not-italic font-medium">— {redeemed.sender_name}</p>}
            </div>
          )}
          <Button
            data-testid="redeem-continue-btn"
            onClick={() => navigate("/chat")}
            className="w-full rounded-full bg-primary text-primary-foreground gap-2"
          >
            Start Using {redeemed.tier_name} <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-md space-y-6 animate-in fade-in">
        <div className="text-center">
          <div className="w-14 h-14 mx-auto rounded-2xl bg-gradient-to-br from-rose-500 to-pink-600 flex items-center justify-center mb-3">
            <Gift className="w-7 h-7 text-white" />
          </div>
          <h1 className="text-2xl font-heading font-bold">Redeem a Gift</h1>
        </div>

        {giftInfo ? (
          <div className="bg-card border border-border/50 rounded-3xl p-5 space-y-4">
            {giftInfo.valid ? (
              <>
                <div className="text-center">
                  <p className="text-lg font-semibold">{giftInfo.tier_name}</p>
                  <p className="text-sm text-muted-foreground">{giftInfo.duration_label}</p>
                </div>
                {giftInfo.note && (
                  <div className="bg-muted/30 rounded-2xl p-3 text-sm italic text-muted-foreground text-center">
                    &ldquo;{giftInfo.note}&rdquo;
                    <p className="text-[11px] mt-1 not-italic">From: {giftInfo.sender_name}</p>
                  </div>
                )}
                <p className="text-[10px] text-muted-foreground text-center">
                  For: {giftInfo.recipient_email_masked} | Expires: {new Date(giftInfo.expires_at).toLocaleDateString()}
                </p>
                {!token ? (
                  <Button onClick={() => navigate("/auth")} className="w-full rounded-full">
                    <Lock className="w-4 h-4 mr-1" /> Log in to Redeem
                  </Button>
                ) : (
                  <Button
                    data-testid="redeem-gift-btn"
                    onClick={handleRedeem}
                    disabled={redeeming}
                    className="w-full rounded-full bg-gradient-to-r from-rose-500 to-pink-600 text-white"
                  >
                    {redeeming ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Gift className="w-4 h-4 mr-1" />}
                    {redeeming ? "Activating..." : "Redeem Gift"}
                  </Button>
                )}
              </>
            ) : (
              <div className="text-center space-y-2">
                <AlertCircle className="w-8 h-8 text-muted-foreground mx-auto" />
                <p className="font-medium">This gift is {giftInfo.status}</p>
                <p className="text-sm text-muted-foreground">
                  {giftInfo.status === "expired" ? "Gifts expire after 7 days" : "This code has already been used"}
                </p>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Enter gift code</label>
              <input
                data-testid="manual-gift-code-input"
                value={manualCode}
                onChange={e => setManualCode(e.target.value.toUpperCase())}
                placeholder="OMNI-XXXX-XXXX"
                className="w-full px-4 py-3 rounded-2xl border border-border/50 bg-card text-center text-lg font-mono font-bold tracking-wider focus:outline-none focus:border-primary"
              />
            </div>
            <Button
              data-testid="lookup-gift-btn"
              onClick={() => {
                if (manualCode.trim()) {
                  if (token) handleRedeem();
                  else { navigate(`/gift/redeem/${manualCode.trim()}`); fetchGiftInfo(manualCode.trim()); }
                }
              }}
              disabled={!manualCode.trim() || redeeming}
              className="w-full rounded-full bg-primary text-primary-foreground"
            >
              {redeeming ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Gift className="w-4 h-4 mr-1" />}
              {token ? "Redeem" : "Look Up Gift"}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
